import React, { memo, useCallback, useLayoutEffect, useRef, useState, useMemo } from 'react';
import classNames from 'classnames';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { ShipstationPalette } from 'ui-components/services/palettes/shipstation-palette';
import { TOOL_TIP_POSITIONS } from 'ui-components/services/tooltip-service';

import { emptyObject } from 'ui-components/default-props/empty';
import { noop } from '../../default-props/noop';
import { generateDataTestidAttribute } from '../../services/testid-service';

import type { ButtonThemeValues, ButtonSizeValues, ButtonComponent, ButtonWrapperComponent, ButtonProps } from './button-types';

import './buttons.less';

export const ButtonSizes: Record<string, ButtonSizeValues> = {
  small: 'small',
  normal: 'normal',
  large: 'large',
};

const buttonSizeToClassMap: Record<ButtonSizeValues, string> = {
  normal: '',
  large: 'large-button',
  small: 'small-button',
};

const getClassNameFromTheme = (theme: ButtonThemeValues): string => {
  const appPalette = ShipstationPalette;
  const buttonClasses = appPalette.buttonClasses as Record<string, string>;
  return buttonClasses[theme] || `button-${theme}`;
};

const getLoadingTheme = (theme: ButtonThemeValues): 'grey' | 'white' => {
  if (theme === '' || theme === THEME_CONSTANTS.tertiary) {
    return 'grey';
  }
  return 'white';
};

const renderLoadingIndicator = (isLoading: boolean, theme: ButtonThemeValues): JSX.Element | undefined => {
  if (!isLoading) {
    return;
  }
  return <LoadingIndicator className='button-loading-indicator' data-testid='button-loading-indicator' theme={getLoadingTheme(theme)} />;
};

const Button: React.FC<ButtonProps> = ({
  onBlur = noop,
  onClick = noop,
  onFocus = noop,
  onKeyDown = noop,
  onMouseEnter = noop,
  onMouseLeave = noop,
  href,
  theme = '',
  disabled = false,
  disabledTooltipMessage,
  disabledTooltipMessagePosition = TOOL_TIP_POSITIONS.top,
  isLightTheme = false,
  disabledTooltipWrapperClassName,
  children = null,
  className = '',
  isLoading = false,
  otherProperties = emptyObject,
  id,
  target = '_blank',
  rel = 'noreferrer noopener',
  tabIndex = 0,
  buttonSize = 'normal',
  style = emptyObject,
  testId,
  ariaLabel,
  type = 'button',
  innerRef,
  pendoTrackingId,
  buttonTitle = null,
  truncateText = false,
  useAdvancedTruncationTitleLogic = false,
}) => {
  const [isTruncated, setIsTruncated] = useState(false);

  const click = useCallback(
    (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
      if (type !== 'submit') {
        e.preventDefault();
      }
      onClick(e);
    },
    [onClick, type],
  );

  const buttonClassnames = classNames(getClassNameFromTheme(theme), className, {
    'is-loading': isLoading,
    [buttonSizeToClassMap[buttonSize]]: theme !== 'unstyled',
    'truncated-button-text': truncateText,
  });
  const additionalProperties = id ? Object.assign({ id }, otherProperties) : otherProperties; // This prevents empty id attributes when Button is not provided an id prop

  const truncationRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    // Check if innerRef is a RefObject (has a current property)
    if (innerRef && typeof innerRef === 'object' && 'current' in innerRef && innerRef.current) {
      const { offsetWidth, scrollWidth } = innerRef.current;
      const spanOffsetWidth = truncationRef?.current?.offsetWidth || 0;
      const styles = getComputedStyle(innerRef.current as unknown as Element);
      const paddingTotal = parseFloat(styles.paddingRight) + parseFloat(styles.paddingLeft) || 0;
      if (spanOffsetWidth + paddingTotal > offsetWidth || scrollWidth > offsetWidth) {
        setIsTruncated(true);
      }
    }
  }, [setIsTruncated, innerRef, truncationRef]);

  // Handle children rendering based on truncation logic and type
  let buttonChildren: React.ReactNode = null;

  if (typeof children !== 'function') {
    // Only apply truncation logic if children is not a function
    if (useAdvancedTruncationTitleLogic) {
      buttonChildren = <span ref={truncationRef}>{children}</span>;
    } else {
      buttonChildren = children;
    }
  }
  // If children is a function, buttonChildren remains null and will be handled in the render path

  const title = useMemo(() => {
    if (useAdvancedTruncationTitleLogic && !isTruncated) {
      return;
    }
    if (isTruncated) {
      const derivedTitle = typeof children === 'string' ? children : undefined;
      return buttonTitle || derivedTitle;
    }
    if (buttonTitle) {
      return buttonTitle;
    }
  }, [isTruncated, buttonTitle, children, useAdvancedTruncationTitleLogic]);

  if (typeof children === 'function') {
    const props = {
      disabled,
      className: buttonClassnames,
      href,
      target,
      rel,
      tabIndex,
      onBlur,
      onClick: click,
      onFocus,
      onKeyDown,
      onMouseEnter,
      onMouseLeave,
      title,
      'data-pendo-id': pendoTrackingId,
      ...additionalProperties,
      ...generateDataTestidAttribute(testId),
      loadingIndicator: renderLoadingIndicator(isLoading, theme),
    };

    return children(props);
  }

  if (href) {
    return (
      <a
        ref={innerRef as React.RefObject<HTMLAnchorElement>}
        className={buttonClassnames}
        data-pendo-id={pendoTrackingId}
        href={href}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        rel={rel}
        style={style}
        tabIndex={tabIndex}
        target={target}
        title={title as string}
        {...additionalProperties}
        {...generateDataTestidAttribute(testId)}
        aria-label={ariaLabel}
      >
        {buttonChildren}
        {renderLoadingIndicator(isLoading, theme)}
      </a>
    );
  }

  const button = (
    <button
      ref={innerRef as React.RefObject<HTMLButtonElement>}
      className={buttonClassnames}
      disabled={disabled}
      onBlur={onBlur}
      onClick={click}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={style}
      tabIndex={tabIndex}
      title={title as string}
      type={type}
      {...additionalProperties}
      {...generateDataTestidAttribute(testId)}
      aria-label={ariaLabel}
      data-pendo-id={pendoTrackingId}
    >
      {buttonChildren}
      {renderLoadingIndicator(isLoading, theme)}
    </button>
  );

  if (disabled && disabledTooltipMessage) {
    return (
      <Tooltip position={disabledTooltipMessagePosition}>
        <span className={classNames('button-with-tooltip', disabledTooltipWrapperClassName)}>{button}</span>
        <TooltipContent lightTheme={isLightTheme}>{disabledTooltipMessage}</TooltipContent>
      </Tooltip>
    );
  }

  return button;
};

const MemoButton: ButtonComponent = memo(Button);

const ButtonWrapper: ButtonWrapperComponent = React.forwardRef((props, ref) => <MemoButton {...props} innerRef={ref} />);

export { ButtonWrapper as Button };
